import { map, Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

/**
 * interfaces
 */
import { IResponse, AppResponse, IAppResponseModel } from '@network';
import { IDataTableRequest } from '../../data-table/interfaces';
import { IDataTableResponseModel } from '../../data-table/models/interfaces';

/**
 * services
 */
import { DataTableSourceService } from '../../data-table/data-table-source.service';
import { DataTableSourceMiddlewareModel } from '../../data-table/models/data-table-source-middleware-model';

/**
 * Base of every upload HTTP layer.
 *
 * It is the only place in the uploads package that touches HttpClient,
 * DataTableSourceService or the response wrapper, so a concrete service
 * below it is left with nothing but its endpoints and its DTO types.
 *
 * It is abstract on purpose: it is never provided or injected as itself,
 * only extended.
 *
 * @example
 * ```typescript
 * @Injectable()
 * export class UploadsApiService extends AbstractUploadApiService {
 *   public getByBatchId(batchId: string) {
 *     return this._get<IUploadBatchDto>(UploadServiceConst.DETAILS_ENDPOINT(batchId));
 *   }
 * }
 * ```
 */
@Injectable()
export abstract class AbstractUploadApiService {

    /** Angular HTTP client. */
    protected readonly _http: HttpClient = inject(HttpClient);

    /**
     * Shared datatable transport. It already bypasses the success toast,
     * so a list load never announces itself.
     */
    protected readonly _dataTableSource: DataTableSourceService = inject(DataTableSourceService);

    /**
     * Sends a GET and wraps the response.
     *
     * @param endpoint - Endpoint to call, relative to the API host.
     * @returns The wrapped response.
     */
    protected _get<TData>(endpoint: string): Observable<IAppResponseModel<TData>> {
        return this._http
            .get<IResponse<TData>>(endpoint, { observe: 'response' })
            .pipe(map((response) => new AppResponse(response)));
    }

    /**
     * Sends a POST and wraps the response.
     *
     * @param endpoint - Endpoint to call, relative to the API host.
     * @param body - Body to send. A FormData body is passed through
     * untouched, so the browser sets the multipart boundary itself.
     * @returns The wrapped response.
     */
    protected _post<TBody, TData>(endpoint: string, body: TBody): Observable<IAppResponseModel<TData>> {
        return this._http
            .post<IResponse<TData>>(endpoint, body, { observe: 'response' })
            .pipe(map((response) => new AppResponse(response)));
    }

    /**
     * Sends a POST carrying the standard datatable body and returns one
     * page of rows.
     *
     * @param endpoint - Endpoint to call, relative to the API host.
     * @param request - The datatable body.
     * @returns The page of rows with its counts.
     */
    protected _postDataTable<TData>(endpoint: string, request: IDataTableRequest): Observable<IDataTableResponseModel<TData>> {
        return this._dataTableSource.dataTableSourceHandler<TData>(endpoint, request);
    }

    /**
     * Puts a set of files into one multipart body under a single
     * repeated field, which is how the upload endpoints read a batch.
     *
     * @param files - The files to send.
     * @param field - Name of the repeated field.
     * @returns The multipart body.
     */
    protected _buildFilesFormData(files: File[], field: string = 'files'): FormData {

        const formData = new FormData();

        for (const file of files) {
            formData.append(field, file, file.name);
        }

        return formData;
    }

    /**
     * Builds a datatable body off the app wide defaults.
     *
     * The uploads list is not driven by a table, so there is no table
     * state to read the paging and sorting from. Going through the
     * defaults keeps the body identical to the one every table sends,
     * with only the differences spelled out by the caller.
     *
     * @param override - Fields to force onto the request, applied last.
     * @returns The request body.
     */
    public static buildListRequest(override: Partial<IDataTableRequest> = {}): IDataTableRequest {
        return {
            ...DataTableSourceMiddlewareModel.defaultBodyRequest,
            ...override,
        };
    }

}
